import React, { useEffect, useState } from 'react';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { DeacParamList } from '../../../typings/Params';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import useFilters from '../../../components/Filter/hooks/useFilters';
import { NativeSyntheticEvent, TextInputKeyPressEventData, View } from 'react-native';
import MyAppText from '../../../components/MyAppText';
import { translate } from '../../../services/translate';
import CustomButton from '../../../components/CustomButton';
import { Tabs } from '../../../components/Tabs';
import FilterInput from '../../../components/Filter/components/FilterInput';
import MyDataTable from '../../../components/MyDataTable';
import { Pagination } from '../../../components/Pagination';
import moment from 'moment';
import Filter from './Filters';
import { WindowInformation } from '../../../services/window-information';
import Toast from 'react-native-toast-message';
import { deacService, VacancyPresenceStatus } from '../../../services/central-api/deac';
import PeriodDropdown, { PeriodType } from '../../../components/deac/PeriodDropdown';
import PresenceRegistrationModal from './PresenceRegistrationModal';
import { getPresenceStatus } from '../Vacancies/VacanciesCommons';
import PresenceCorrectionModal from './PresenceCorrectionModal';
import Tooltip from '../../../components/Tooltip';
import { useAuth } from '../../../context/Auth';
import PresenceControlReport from './PresenceControlReport';
import MyDateRangePicker, { Value, ValuePiece } from '../../../components/MyDateRangePicker';
import { parseTextWithBold } from '../DeacCommons';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { useQuery } from '@tanstack/react-query';

export default function PresenceControl({ navigation }: { navigation: Navigation; }) {
    const { styles, theme } = useStyles(styleSheet);
    const route = useRoute<RouteProp<DeacParamList, 'PresenceControl'>>();
    const { user: authenticatedUser } = useAuth();
    const windowInfo = WindowInformation();

    const [filters, setFilters] = useFilters<DeacParamList['PresenceControl']>({
        ...route.params,
        page: Math.max(+route.params.page - 1, 0),
        limit: Number(route.params.limit) || 25,
    });
    const [filtersZIndex, setFiltersZIndex] = useState<number>(1);
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [registerPresenceModal, setRegisterPresenceModal] = useState<boolean>(false);
    const [clearSelectedRows, setClearSelectedRows] = useState<boolean>(false);
    const [selectedApplications, setSelectedApplications] = useState<Presence[]>([]);
    const [displayType, setDisplayType] = useState<VacancyPresenceStatus>('pending');
    const [oldDisplayType, setOldDisplayType] = useState<VacancyPresenceStatus>('pending');
    const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>('allPeriod');
    const [dateFrom, setDateFrom] = useState<ValuePiece>(null);
    const [dateTo, setDateTo] = useState<ValuePiece>(null);

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['deacPresenceControl', filters],
        queryFn: () => deacService.getPresences({
            ...filters,
            type: displayType
        }),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch, isError, error } = queryResult;

    useEffect(() => {
        if (isError) {
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }, [error, isError]);

    useEffect(() => {
        if (oldDisplayType != displayType) {
            setClearSelectedRows(true);
            setSelectedApplications([]);

            navigation.setParams({
                ...filters,
                page: 1,
            });

            setFilters({
                ...filters,
                page: 0,
            });
        }
    }, [displayType]);

    useEffect(() => {
        if (selectedPeriod != 'select') {
            let beginIn = undefined;
            const endIn = moment().valueOf();

            if (selectedPeriod === 'today') {
                beginIn = moment().startOf('day').valueOf();
            } else if (selectedPeriod === 'weekly') {
                beginIn = moment().startOf('day').subtract(7, 'days').valueOf();
            } else if (selectedPeriod === 'fortnightly') {
                beginIn = moment().startOf('day').subtract(15, 'days').valueOf();
            } else if (selectedPeriod === 'monthly') {
                beginIn = moment().startOf('day').subtract(30, 'days').valueOf();
            } else if (selectedPeriod === 'semiannual') {
                beginIn = moment().startOf('day').subtract(6, 'months').valueOf();
            }

            navigation.setParams({
                ...filters,
                page: 1,
                beginIn: beginIn,
                endIn: endIn
            });

            setFilters({
                ...filters,
                page: 0,
                beginIn: beginIn,
                endIn: endIn
            });
        }
    }, [selectedPeriod]);

    function setBeginInAndEndIn(beginIn: number | undefined, endIn: number) {
        navigation.setParams({
            ...filters,
            page: 1,
            beginIn: beginIn,
            endIn: endIn
        });

        setFilters({
            ...filters,
            page: 0,
            beginIn: beginIn,
            endIn: endIn
        });
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    function getPeriodMessage() {
        if (selectedPeriod === 'today') {
            return translate('today');
        } else if (selectedPeriod === 'weekly') {
            return translate('last7days');
        } else if (selectedPeriod === 'fortnightly') {
            return translate('last15days');
        } else if (selectedPeriod === 'monthly') {
            return translate('last30days');
        } else if (selectedPeriod === 'semiannual') {
            return translate('last6Months');
        }

        return translate('allPeriod');
    }

    useEffect(() => {
        if (clearSelectedRows) {
            setClearSelectedRows(false);
        }
    }, [clearSelectedRows]);

    function displayPresences(type: VacancyPresenceStatus) {
        setOldDisplayType(displayType);
        setDisplayType(type);
        return <Card style={{ padding: 0 }}>
            <DataTableHeader
                title={translate(type === 'pending'
                    ? 'agentsWaitingConfirmation'
                    : type === 'present'
                        ? 'agentsWithConfirmedPresence'
                        : 'agentsWithConfirmedAbsence'
                )}
                zIndex={1}
                filterInput={<FilterInput
                    value={textFilter}
                    placeholder={translate('typeToSearch')}
                    onChange={setTextFilter}
                    onKeyPress={handleKeyDown}
                />}
                filter={<Filter
                    filters={filters}
                    setFilters={setFilters}
                    setTextFilter={setTextFilter}
                    textFilter={textFilter}
                    type={displayType}
                    onStatusChange={(isOpen) => {
                        if (isOpen) {
                            setFiltersZIndex(3);
                        } else {
                            setFiltersZIndex(1);
                        }
                    }}
                />}
                actions={<CustomButton
                    text={translate(type === 'pending' ? 'registerParticipation' : 'fixParticipation')}
                    onPress={() => {
                        setRegisterPresenceModal(true);
                    }}
                    disabled={selectedApplications.length === 0}
                />}
            />
            <DataTableContainer
                dataTable={<MyDataTable
                    clearSelectedRows={clearSelectedRows}
                    selectableRows
                    selectableRowDisabled={(row) => {
                        if (row.present === null) {
                            return false;
                        }
                        const isAvailableTime = row.vacancy && moment().diff(moment(row.vacancy.activityFinish), 'hours') <= 48;
                        const hasAlwaysEditPermission = authenticatedUser?.permissions.deac_manage_always_fix_presence;
                        return !(isAvailableTime || hasAlwaysEditPermission);
                    }}
                    onSelectedRowsChange={(selectedRows) => {
                        setSelectedApplications(selectedRows.selectedRows);
                    }}
                    columns={[
                        {
                            name: translate('status'),
                            cell: row => getPresenceStatus(row),
                            grow: 0,
                        },
                        {
                            name: translate('type'),
                            cell: row => row.absentJustification ? translate('justifiedAbsence') : translate('unjustifiedAbsence'),
                            grow: 1,
                            omit: type === 'pending' || type === 'present'
                        },
                        {
                            name: translate('agentName'),
                            cell: row => row.user.warName && row.user.warName != '' ? row.user.warName : row.user.name,
                            grow: 1,
                        },
                        {
                            name: 'RF',
                            cell: row => row.user.registry,
                            grow: 1,
                        },
                        {
                            name: translate('activityDate'),
                            cell: row => moment(row.vacancy.activityStart).format('DD/MM/YYYY HH:mm'),
                            grow: 1,
                        },
                        {
                            name: translate('assignment'),
                            cell: row => {
                                if (row.assignment) {
                                    return row.assignment.name;
                                }
                                return '';
                            },
                            grow: 1,
                            omit: type !== 'present'
                        },
                        {
                            name: translate('role'),
                            cell: row => row.user.role.name,
                            grow: 1,
                        },
                        {
                            name: translate('vacancyId'),
                            cell: row => row.vacancy.id,
                            grow: 0,
                            wrap: true
                        },
                        {
                            name: translate('activityTitle'),
                            cell: row => row.vacancy.title,
                            grow: 1,
                            wrap: true
                        },
                        {
                            name: translate('workplace'),
                            cell: row => <Tooltip
                                useSimpleBox
                                style={{
                                    color: theme.colors.white['500'],
                                    padding: 16,
                                    borderRadius: 12,
                                    justifyContent: 'center',
                                    zIndex: 9999,
                                    background: theme.colors.neutral['500'],
                                    width: 250
                                }}
                                tooltipContent={
                                    <View style={{ flexDirection: 'column', }}>
                                        <View style={{ flexDirection: 'row', columnGap: 3 }}>
                                            <MyAppText style={styles.tooltipTextBold}>{translate('actingBody')}: </MyAppText>
                                            <MyAppText style={styles.tooltipText}>{row.vacancy.workplace.actingBodyUnit.actingBody.name}</MyAppText>
                                        </View>
                                        <View style={{ flexDirection: 'row', columnGap: 3 }}>
                                            <MyAppText style={styles.tooltipTextBold}>{translate('actingBodyCommand')}: </MyAppText>
                                            <MyAppText style={styles.tooltipText}>{row.vacancy.workplace.actingBodyCommand.name}</MyAppText>
                                        </View>
                                        <View style={{ flexDirection: 'row', columnGap: 3 }}>
                                            <MyAppText style={styles.tooltipTextBold}>{translate('actingBodyUnit')}: </MyAppText>
                                            <MyAppText style={styles.tooltipText}>{row.vacancy.workplace.actingBodyUnit.name}</MyAppText>
                                        </View>
                                    </View>
                                }
                                tooltipDirection='right'
                            >
                                {row.vacancy.workplace.name}
                            </Tooltip>,
                            grow: 1,
                            wrap: true
                        },
                        {
                            name: translate('agentActingBody'),
                            cell: row => row.user.actingBodyUnit.actingBody.name,
                            grow: 1,
                        },
                        {
                            name: translate('agentUnit'),
                            cell: row => row.user.actingBodyUnit.name,
                            grow: 1,
                        },
                        {
                            name: translate('registeredBy'),
                            cell: row => row.presenceRegisteredBy?.warName && row.presenceRegisteredBy.warName != '' ? row.presenceRegisteredBy.warName : row.presenceRegisteredBy?.name ?? '',
                            grow: 1,
                            omit: type === 'pending'
                        },
                        {
                            name: translate('registeredIn'),
                            cell: row => row.presenceRegistration ? moment(row.presenceRegistration).format('DD/MM/YYYY') : '',
                            grow: 1,
                            omit: type === 'pending'
                        },
                        {
                            name: translate('changedBy'),
                            cell: row => row.presenceChangedBy?.warName && row.presenceChangedBy.warName != '' ? row.presenceChangedBy.warName : row.presenceChangedBy?.name ?? '',
                            grow: 1,
                            omit: type === 'pending'
                        },
                        {
                            name: translate('changedIn'),
                            cell: row => row.presenceAlteration ? moment(row.presenceAlteration).format('DD/MM/YYYY') : '',
                            grow: 1,
                            omit: type === 'pending'
                        }
                    ]}
                    data={data?.rows || []}
                    progressPending={isLoading}
                />}
                pagination={<Pagination
                    currentPage={filters.page}
                    totalItems={data?.count || 0}
                    setPage={page => {
                        setFilters(old => ({ ...old, page }));
                    }}
                    pageSize={filters.limit}
                    setPageSize={pageSize => {
                        setFilters(old => ({ ...old, limit: pageSize, page: 0 }));
                    }}
                />}
            />
        </Card>;
    }

    return (
        <Container>
            <Header
                title={translate('presenceControl')}
                zIndex={2}
                actions={<View style={{ flexDirection: 'row', gap: 8 }}>
                    <MyDateRangePicker
                        dateRangePickerStyle={{
                            borderWidth: 1,
                            borderColor: theme.colors.neutral['300']
                        }}
                        calendarPosition='left'
                        useCustomDateInfo={true}
                        dateFormat={'DD/MM'}
                        dateFrom={dateFrom}
                        setDateFrom={setDateFrom}
                        dateTo={dateTo}
                        setDateTo={setDateTo}
                        onChange={(value: Value) => {
                            if (Array.isArray(value)) {
                                setSelectedPeriod('select');

                                if (value[0] && value[1]) {
                                    const beginIn = moment(value[0]).startOf('day').valueOf();
                                    const endIn = moment(value[1]).endOf('day').valueOf();
                                    setBeginInAndEndIn(beginIn, endIn);
                                }
                            }
                        }}
                        onRemove={() => {
                            setDateFrom(null);
                            setDateTo(null);
                            setSelectedPeriod('allPeriod');
                        }}
                    />
                    <PeriodDropdown
                        setValue={setSelectedPeriod}
                        value={selectedPeriod}
                        zIndex={2}
                    />
                    {data?.summary && <PresenceControlReport
                        displayType={displayType}
                        filters={filters}
                        summary={data.summary}
                        periodMessage={getPeriodMessage()}
                    />}
                </View>}
            />
            <View style={styles.preview(windowInfo.isMobile)}>
                <Card style={{ borderWidth: 1, borderColor: theme.colors.neutral['200'] }}>
                    <View style={styles.summaryHeader}>
                        <MyAppText style={styles.summaryHeaderText}>{parseTextWithBold(translate('pendingParticipation'), '#AC6423')}</MyAppText>
                        <MyAppText>{getPeriodMessage()}</MyAppText>
                    </View>
                    <MyAppText style={styles.summaryText('#AC6423')}>{data?.summary.pending}</MyAppText>
                </Card>
                <Card style={{ borderWidth: 1, borderColor: theme.colors.neutral['200'] }}>
                    <View style={styles.summaryHeader}>
                        <MyAppText style={styles.summaryHeaderText}>{parseTextWithBold(translate('presenceSummary'), '#458133')}</MyAppText>
                        <MyAppText>{getPeriodMessage()}</MyAppText>
                    </View>
                    <MyAppText style={styles.summaryText('#458133')}>{data?.summary.presences}</MyAppText>
                </Card>
                <Card style={{ borderWidth: 1, borderColor: theme.colors.neutral['200'] }}>
                    <View style={styles.summaryHeader}>
                        <MyAppText style={styles.summaryHeaderText}>{parseTextWithBold(translate('absenceSummary'), '#B53333')}</MyAppText>
                        <MyAppText>{getPeriodMessage()}</MyAppText>
                    </View>
                    <MyAppText style={styles.summaryText('#B53333')}>{data?.summary.absences}</MyAppText>
                </Card>
            </View>
            <Card style={{ flex: 1, zIndex: filtersZIndex, marginTop: 16 }}>
                <Tabs
                    tabs={[{
                        key: 'pendingConfirmations',
                        label: translate('pendingConfirmations'),
                    }, {
                        key: 'confirmedPresences',
                        label: translate('confirmedPresences'),
                    }, {
                        key: 'confirmedAbsences',
                        label: translate('confirmedAbsences'),
                    }]}
                >
                    {({ selectedTab }) => {
                        if (selectedTab == 'pendingConfirmations') {
                            return displayPresences('pending');
                        } else if (selectedTab == 'confirmedPresences') {
                            return displayPresences('present');
                        } else if (selectedTab == 'confirmedAbsences') {
                            return displayPresences('absent');
                        }
                    }}
                </Tabs>
            </Card>
            {displayType === 'pending'
                ? <PresenceRegistrationModal
                    isModalVisible={registerPresenceModal}
                    setModalVisible={setRegisterPresenceModal}
                    onConfirm={() => {
                        refetch();
                    }}
                    onClose={() => {
                        setClearSelectedRows(true);
                        setRegisterPresenceModal(false);
                        setSelectedApplications([]);
                    }}
                    selectedApplications={selectedApplications}
                    setSelectedApplications={setSelectedApplications}
                />
                : <PresenceCorrectionModal
                    isModalVisible={registerPresenceModal}
                    setModalVisible={setRegisterPresenceModal}
                    onConfirm={() => {
                        refetch();
                    }}
                    onClose={() => {
                        setClearSelectedRows(true);
                        setRegisterPresenceModal(false);
                        setSelectedApplications([]);
                    }}
                    selectedApplications={selectedApplications}
                    setSelectedApplications={setSelectedApplications}
                    fixFor={displayType}
                />
            }
        </Container>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    preview: (isMobile: boolean) => ({
        flexDirection: isMobile ? 'column' : 'row',
        gap: 8,
    }),
    summary: {
        flex: 1,
        flexDirection: 'column',
        rowGap: 16,
        borderRadius: 4,
        borderWidth: 1,
        borderColor: theme.colors.neutral['200'],
        paddingHorizontal: 16,
        paddingVertical: 12,
        backgroundColor: theme.colors.white['500']
    },
    summaryHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between'
    },
    summaryHeaderText: {
        fontSize: 16,
        fontWeight: '600',
        color: theme.colors.neutral['400']
    },
    summaryText: (color: string) => ({
        fontSize: 18,
        fontWeight: '700',
        color: color
    }),
    tooltipTextBold: {
        fontSize: 14,
        fontWeight: '700',
        color: theme.colors.white['500']
    },
    tooltipText: {
        fontSize: 14,
        fontWeight: '500',
        color: theme.colors.white['500']
    },
}));
